import { useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { Link, useParams } from 'react-router-dom';
import { PageHeader, Panel, PanelBody, PanelHeader } from '../../components/ui/Panel';
import Button from '../../components/ui/Button';
import Pill from '../../components/ui/Pill';
import { ErrorNotice, InfoNotice, LoadingPanel } from '../../components/ui/States';
import { HeadRow, Td, Th, inputClass } from '../../components/review/parts';
import {
  CycleDetail,
  STATES,
  SEAT_LABELS,
  Seat,
  errorMessage,
  staffApi,
  staffBase,
  stateLabel,
} from '../../services/reviewApi';
import { ApplicationsTab, AssignmentTab, AuditTab, CalibrationTab, ConflictsTab, DeliberationTab } from './CycleWork';
import { useCycleRefresh } from './useCycleRefresh';
import { promptAction } from '../../components/ui/Confirm';

type Tab =
  'overview' | 'committee' | 'applications' | 'conflicts' | 'assignment' | 'calibration' | 'deliberation' | 'audit';

const TABS: { key: Tab; label: string; needs?: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'committee', label: 'Committee' },
  { key: 'applications', label: 'Applications' },
  { key: 'conflicts', label: 'Conflicts' },
  { key: 'assignment', label: 'Assignment', needs: 'progress.view' },
  { key: 'calibration', label: 'Calibration', needs: 'calibration.view' },
  { key: 'deliberation', label: 'Deliberation', needs: 'deliberation.view' },
  { key: 'audit', label: 'Audit' },
];

function Transition({ cycle }: { cycle: CycleDetail }) {
  const refresh = useCycleRefresh(cycle.id);
  const [reason, setReason] = useState('');
  const [force, setForce] = useState(false);
  const move = useMutation(
    async (to: string) =>
      (await staffApi.post(`${staffBase}/cycles/${cycle.id}/transition`, { to, reason, force })).data.data,
    {
      onSuccess: () => {
        setReason('');
        setForce(false);
        refresh();
      },
    },
  );
  return (
    <Panel>
      <PanelHeader title="State" note={stateLabel(cycle.state)} />
      <PanelBody>
        <ol className="flex flex-wrap gap-1 mb-5">
          {STATES.map((s, i) => (
            <li
              key={s}
              className={`font-semibold text-small px-2 py-[5px] border ${
                s === cycle.state
                  ? 'bg-accent-tint border-accent'
                  : i < STATES.indexOf(cycle.state)
                    ? 'bg-paper border-line text-ink-2'
                    : 'bg-white border-line text-ink-2'
              }`}
            >
              {stateLabel(s)}
            </li>
          ))}
        </ol>
        {cycle.can['cycle.transition'] ? (
          <>
            <label className="block text-small text-ink-2 mb-1">
              Reason (required to go back or to move on with reviews missing)
            </label>
            <input className={`${inputClass} mb-3`} value={reason} onChange={(e) => setReason(e.target.value)} />
            {cycle.next === 'calibration' ? (
              <label className="flex items-center gap-2 text-small mb-3">
                <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} /> Move on even if
                some reviews are not submitted
              </label>
            ) : null}
            <div className="flex gap-3">
              {cycle.next ? (
                <Button variant="primary" disabled={move.isLoading} onClick={() => move.mutate(cycle.next!)}>
                  Move to {stateLabel(cycle.next)}
                </Button>
              ) : null}
              {cycle.previous ? (
                <Button
                  variant="ghost"
                  disabled={move.isLoading || !reason.trim()}
                  onClick={() => move.mutate(cycle.previous!)}
                >
                  Back to {stateLabel(cycle.previous)}
                </Button>
              ) : null}
            </div>
            {move.error ? (
              <div className="mt-3">
                <ErrorNotice>{errorMessage(move.error, 'Could not change the state.')}</ErrorNotice>
              </div>
            ) : null}
          </>
        ) : (
          <p className="text-small text-ink-2">This cycle is archived.</p>
        )}
      </PanelBody>
    </Panel>
  );
}

function SettingsPanel({ cycle }: { cycle: CycleDetail }) {
  const refresh = useCycleRefresh(cycle.id);
  const s = cycle.settings;
  const [form, setForm] = useState({
    discrepancyThreshold: String(s.discrepancyThreshold),
    reviewersPerApplication: String(s.reviewersPerApplication),
    selfIdDuringScoring: s.selfIdDuringScoring,
    reviewerGroups: s.reviewerGroups.join(', '),
    acceptedGroups: s.acceptedGroups.join(', '),
  });
  const [editions, setEditions] = useState(cycle.editions.map((e) => e.id));
  useEffect(() => setEditions(cycle.editions.map((e) => e.id)), [cycle.editions]);
  const save = useMutation(
    async (body: Record<string, unknown>) => (await staffApi.patch(`${staffBase}/cycles/${cycle.id}`, body)).data.data,
    { onSuccess: refresh },
  );
  const split = (v: string) =>
    v
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean);
  const editable = cycle.can['cycle.edit_settings'];

  return (
    <Panel>
      <PanelHeader title="Settings" />
      <PanelBody>
        <div className="text-small text-ink-2 mb-1">Fellow application form(s)</div>
        {cycle.availableEditions.length ? (
          cycle.availableEditions.map((e) => (
            <label key={e.id} className="flex items-center gap-2 text-table py-[3px]">
              <input
                type="checkbox"
                disabled={!cycle.can['cycle.edit_eligibility']}
                checked={editions.includes(e.id)}
                onChange={(ev) =>
                  setEditions(ev.target.checked ? [...editions, e.id] : editions.filter((x) => x !== e.id))
                }
              />
              {e.label} <span className="text-ink-2">(form {e.formId})</span>
            </label>
          ))
        ) : (
          <p className="text-small text-ink-2">
            No Fellow application forms yet. They appear here once the website form exists.
          </p>
        )}
        {cycle.can['cycle.edit_eligibility'] ? (
          <Button size="sm" variant="outline" className="mt-2" onClick={() => save.mutate({ editionIds: editions })}>
            Save forms
          </Button>
        ) : null}

        <div className="grid grid-cols-2 gap-4 mt-5">
          <div>
            <label className="block text-small text-ink-2 mb-1">Third-review threshold (priority points apart)</label>
            <input
              className={inputClass}
              disabled={!editable}
              value={form.discrepancyThreshold}
              onChange={(e) => setForm({ ...form, discrepancyThreshold: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-small text-ink-2 mb-1">Reviewers per application</label>
            <input
              className={inputClass}
              disabled={!editable}
              value={form.reviewersPerApplication}
              onChange={(e) => setForm({ ...form, reviewersPerApplication: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-small text-ink-2 mb-1">Reviewer groups</label>
            <input
              className={inputClass}
              disabled={!editable}
              value={form.reviewerGroups}
              onChange={(e) => setForm({ ...form, reviewerGroups: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-small text-ink-2 mb-1">
              Accepted applicants join ({'{year}'} is the cycle year)
            </label>
            <input
              className={inputClass}
              disabled={!editable}
              value={form.acceptedGroups}
              onChange={(e) => setForm({ ...form, acceptedGroups: e.target.value })}
            />
          </div>
        </div>
        <label className="flex items-center gap-2 text-table mt-4">
          <input
            type="checkbox"
            disabled={!editable}
            checked={form.selfIdDuringScoring}
            onChange={(e) => setForm({ ...form, selfIdDuringScoring: e.target.checked })}
          />
          Show self-identification to reviewers while they score (default: off; shown per applicant at deliberation)
        </label>
        <p className="text-small text-ink-2 mt-3">
          Retention: unsuccessful applications {s.retention.unsuccessfulYears} years after the decision; accepted{' '}
          {s.retention.fellowshipYears} + {s.retention.acceptedYearsAfterFellowship} years; audit{' '}
          {s.retention.auditYears} years.
        </p>
        {editable ? (
          <Button
            size="sm"
            variant="outline"
            className="mt-3"
            onClick={() =>
              save.mutate({
                settings: {
                  discrepancyThreshold: Number(form.discrepancyThreshold),
                  reviewersPerApplication: Number(form.reviewersPerApplication),
                  selfIdDuringScoring: form.selfIdDuringScoring,
                  reviewerGroups: split(form.reviewerGroups),
                  acceptedGroups: split(form.acceptedGroups),
                },
              })
            }
          >
            Save settings
          </Button>
        ) : null}
        {save.error ? (
          <div className="mt-3">
            <ErrorNotice>{errorMessage(save.error, 'Could not save.')}</ErrorNotice>
          </div>
        ) : null}
      </PanelBody>
    </Panel>
  );
}

function JsonEditor({
  cycle,
  field,
  title,
  editable,
  note,
}: {
  cycle: CycleDetail;
  field: 'rubric' | 'eligibilityRules';
  title: string;
  editable: boolean;
  note: string;
}) {
  const refresh = useCycleRefresh(cycle.id);
  const value = cycle[field];
  const [text, setText] = useState(JSON.stringify(value, null, 2));
  const [editing, setEditing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const save = useMutation(
    async (body: unknown) => (await staffApi.patch(`${staffBase}/cycles/${cycle.id}`, { [field]: body })).data.data,
    {
      onSuccess: () => {
        setEditing(false);
        refresh();
      },
    },
  );
  return (
    <Panel>
      <PanelHeader title={title} note={`version ${value.version}`}>
        {editable && !editing ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setText(JSON.stringify(value, null, 2));
              setEditing(true);
            }}
          >
            Edit
          </Button>
        ) : null}
      </PanelHeader>
      <PanelBody>
        <p className="text-small text-ink-2 mb-3">{note}</p>
        {editing ? (
          <>
            <textarea
              className={`${inputClass} font-mono text-small h-[320px]`}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <div className="flex gap-3 mt-3">
              <Button
                size="sm"
                variant="primary"
                onClick={() => {
                  try {
                    const parsed = JSON.parse(text);
                    setParseError(null);
                    save.mutate(parsed);
                  } catch {
                    setParseError('That is not valid JSON.');
                  }
                }}
              >
                Save as a new version
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
            {parseError || save.error ? (
              <div className="mt-3">
                <ErrorNotice>{parseError ?? errorMessage(save.error, 'Could not save.')}</ErrorNotice>
              </div>
            ) : null}
          </>
        ) : field === 'rubric' ? (
          <table className="w-full">
            <tbody>
              {cycle.rubric.criteria.map((c) => (
                <tr key={c.key} className="border-b border-line">
                  <Td className="text-ink-2 w-[160px]">{c.section}</Td>
                  <Td>{c.label}</Td>
                  <Td className="text-ink-2">
                    {c.scale === 'lmh'
                      ? 'L/M/H'
                      : c.scale === 'count_lmh'
                        ? 'count + L/M/H'
                        : c.scale === 'score'
                          ? `${c.min ?? 0}–${c.max}`
                          : 'text'}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <ul className="text-table">
            {cycle.eligibilityRules.rules.map((r) => (
              <li key={String(r.key)} className="py-1 border-b border-line">
                {String(r.label ?? r.key)}
              </li>
            ))}
          </ul>
        )}
      </PanelBody>
    </Panel>
  );
}

function Committee({ cycle }: { cycle: CycleDetail }) {
  const refresh = useCycleRefresh(cycle.id);
  const [seat, setSeat] = useState<Seat>('reviewer');
  const [q, setQ] = useState('');
  const [emailText, setEmailText] = useState('');
  const [label, setLabel] = useState('Patient Partner');
  const editable = cycle.can['committee.manage'];
  const committeeSeat = seat === 'chair' || seat === 'co_chair' || seat === 'reviewer';
  const candidates = useQuery<{ userId: string; name: string; email: string; groups: string[] }[]>(
    ['review', cycle.id, 'candidates', q],
    async () => (await staffApi.get(`${staffBase}/cycles/${cycle.id}/candidates`, { params: { q } })).data.data,
    { enabled: editable && committeeSeat },
  );
  const add = useMutation(
    async (body: Record<string, unknown>) =>
      (await staffApi.post(`${staffBase}/cycles/${cycle.id}/committee`, body)).data.data,
    {
      onSuccess: () => {
        setEmailText('');
        refresh();
      },
    },
  );
  const remove = useMutation(
    async ({ id, reason }: { id: string; reason: string }) =>
      staffApi.delete(`${staffBase}/cycles/${cycle.id}/committee/${id}`, { data: { reason } }),
    { onSuccess: refresh },
  );
  const updateEmails = useMutation(
    async ({ id, otherEmails }: { id: string; otherEmails: string }) =>
      staffApi.patch(`${staffBase}/cycles/${cycle.id}/committee/${id}`, { otherEmails }),
    { onSuccess: refresh },
  );
  const seated = new Set(cycle.committee.filter((m) => m.seat === seat).map((m) => m.userId));

  return (
    <div className="grid grid-cols-[1fr_380px] gap-6 items-start">
      <Panel>
        <PanelHeader title="Committee" note={`${cycle.committee.length} seats`} />
        <table className="w-full">
          <thead>
            <HeadRow>
              <Th>Name</Th>
              <Th>Seat</Th>
              <Th>Assigned</Th>
              <Th>Other emails (for supervisor matching)</Th>
              <Th />
            </HeadRow>
          </thead>
          <tbody>
            {cycle.committee.map((m) => (
              <tr key={m.id} className="border-b border-line">
                <Td>
                  {m.name}
                  <div className="text-small text-ink-2">{m.email}</div>
                </Td>
                <Td>
                  <Pill tone={m.seat === 'program_manager' ? 'running' : m.seat === 'observer' ? 'inactive' : 'active'}>
                    {SEAT_LABELS[m.seat]}
                  </Pill>
                  {m.seatLabel ? <div className="text-small text-ink-2 mt-1">{m.seatLabel}</div> : null}
                </Td>
                <Td className="tabular">{m.seat === 'reviewer' ? m.activeAssignments : ''}</Td>
                <Td>
                  {m.userId && editable ? (
                    <input
                      className={`${inputClass} text-small py-[5px]`}
                      defaultValue={m.otherEmails.join(', ')}
                      placeholder="none"
                      onBlur={(e) => {
                        if (e.target.value !== m.otherEmails.join(', '))
                          updateEmails.mutate({ id: m.id, otherEmails: e.target.value });
                      }}
                    />
                  ) : (
                    <span className="text-small">{m.otherEmails.join(', ')}</span>
                  )}
                </Td>
                <Td>
                  {editable ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={async () => {
                        const reason = await promptAction(`Remove ${m.name} (${SEAT_LABELS[m.seat]})? Reason:`);
                        if (reason) remove.mutate({ id: m.id, reason });
                      }}
                    >
                      Remove
                    </Button>
                  ) : null}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
        {remove.error || updateEmails.error ? (
          <div className="p-5">
            <ErrorNotice>
              {errorMessage(remove.error ?? updateEmails.error, 'Could not change the committee.')}
            </ErrorNotice>
          </div>
        ) : null}
      </Panel>

      {editable ? (
        <Panel>
          <PanelHeader title="Add a seat" />
          <PanelBody>
            <label className="block text-small text-ink-2 mb-1">Seat</label>
            <select className={`${inputClass} mb-3`} value={seat} onChange={(e) => setSeat(e.target.value as Seat)}>
              {(['reviewer', 'chair', 'co_chair', 'observer', 'program_manager'] as Seat[]).map((s) => (
                <option key={s} value={s}>
                  {SEAT_LABELS[s]}
                </option>
              ))}
            </select>
            {committeeSeat ? (
              <>
                <input
                  className={`${inputClass} mb-2`}
                  placeholder="Search PIs and Co-Is by name or email"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
                <ul className="max-h-[320px] overflow-y-auto border border-line">
                  {(candidates.data ?? []).map((c) => (
                    <li
                      key={c.userId}
                      className="flex items-center justify-between gap-2 px-3 py-[7px] border-b border-line text-small"
                    >
                      <span>
                        {c.name}
                        <span className="block text-small text-ink-2">{c.groups.join(', ')}</span>
                      </span>
                      {seated.has(c.userId) ? (
                        <span className="text-small text-ink-2">seated</span>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => add.mutate({ userId: c.userId, seat })}>
                          Add
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
                <p className="text-small text-ink-2 mt-2">
                  Reviewers, chairs and co-chairs come from the {cycle.settings.reviewerGroups.join(' and ')} groups.
                </p>
              </>
            ) : (
              <>
                <label className="block text-small text-ink-2 mb-1">
                  {seat === 'program_manager' ? 'Operator email' : 'Member email'}
                </label>
                <input
                  className={`${inputClass} mb-3`}
                  value={emailText}
                  onChange={(e) => setEmailText(e.target.value)}
                />
                {seat === 'observer' ? (
                  <>
                    <label className="block text-small text-ink-2 mb-1">Seat label</label>
                    <select className={`${inputClass} mb-3`} value={label} onChange={(e) => setLabel(e.target.value)}>
                      <option>Patient Partner</option>
                      <option>Fellow Representative</option>
                    </select>
                  </>
                ) : null}
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!emailText.trim()}
                  onClick={() =>
                    add.mutate(
                      seat === 'program_manager'
                        ? { adminEmail: emailText, seat }
                        : { email: emailText, seat, seatLabel: label },
                    )
                  }
                >
                  Add
                </Button>
                <p className="text-small text-ink-2 mt-2">
                  {seat === 'observer'
                    ? 'Observers see the ranked list and the aggregate self-identification summary at deliberation only.'
                    : 'A Program Manager is an admin operator. They run the cycle, see everything and never score.'}
                </p>
              </>
            )}
            {add.error ? (
              <div className="mt-3">
                <ErrorNotice>{errorMessage(add.error, 'Could not add the seat.')}</ErrorNotice>
              </div>
            ) : null}
          </PanelBody>
        </Panel>
      ) : null}
    </div>
  );
}

export default function FellowReviewCycle() {
  const { cycleId = '' } = useParams();
  const [tab, setTab] = useState<Tab>('overview');
  const cycle = useQuery<CycleDetail>(
    ['review-cycle', cycleId],
    async () => (await staffApi.get(`${staffBase}/cycles/${cycleId}`)).data.data,
  );

  if (cycle.isLoading) return <LoadingPanel label="Loading the cycle…" />;
  if (cycle.error || !cycle.data) {
    return (
      <div>
        <PageHeader title="Fellow review" />
        <ErrorNotice>{errorMessage(cycle.error, 'Could not load the cycle.')}</ErrorNotice>
      </div>
    );
  }
  const c = cycle.data;
  const tabs = TABS.filter((t) => !t.needs || c.can[t.needs]);

  return (
    <div>
      <PageHeader
        eyebrow={
          <Link to="/fellow-review" className="link-underline text-small text-ink-2">
            All cycles
          </Link>
        }
        title={c.name}
        sub={`${stateLabel(c.state)} · ${Object.values(c.applicationCounts).reduce((a, b) => a + b, 0)} applications · ${c.committee.filter((m) => m.seat === 'reviewer').length} reviewers`}
      />
      <div className="flex gap-1 border-b border-line-strong mb-6">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`font-semibold text-small px-4 py-[9px] ${tab === t.key ? 'bg-ink text-paper' : 'text-ink-2 hover:text-ink'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' ? (
        <div className="grid grid-cols-2 gap-6 items-start">
          <div className="flex flex-col gap-6">
            <Transition cycle={c} />
            <JsonEditor
              cycle={c}
              field="eligibilityRules"
              title="Eligibility rules"
              editable={c.can['cycle.edit_eligibility']}
              note="Run over the form's structured answers before assignment. Anything that does not clearly pass is flagged for you to resolve."
            />
          </div>
          <div className="flex flex-col gap-6">
            <SettingsPanel cycle={c} />
            <JsonEditor
              cycle={c}
              field="rubric"
              title="Rubric"
              editable={c.can['cycle.edit_rubric'] && c.reviewsStarted === 0}
              note="Fixed once the first reviewer attests. Each review records the rubric version it was scored with."
            />
          </div>
        </div>
      ) : null}
      {tab === 'committee' ? <Committee cycle={c} /> : null}
      {tab === 'applications' ? <ApplicationsTab cycle={c} /> : null}
      {tab === 'conflicts' ? <ConflictsTab cycle={c} /> : null}
      {tab === 'assignment' ? <AssignmentTab cycle={c} /> : null}
      {tab === 'calibration' ? <CalibrationTab cycle={c} /> : null}
      {tab === 'deliberation' ? <DeliberationTab cycle={c} /> : null}
      {tab === 'audit' ? <AuditTab cycle={c} /> : null}
      {c.state === 'archived' ? (
        <div className="mt-6">
          <InfoNotice>
            Archived: read-only. Reviewers no longer have access. Retention is applied with npm run purge-review-data.
          </InfoNotice>
        </div>
      ) : null}
    </div>
  );
}
